export type TelemetryRuntime = "client" | "server";

export type TelemetryFramework =
  | "browser"
  | "react"
  | "react-router"
  | "vue2"
  | "vue3"
  | "nuxt"
  | "angular";

/** Severity for log/message events and breadcrumb entries */
export type TelemetryLevel =
  | "trace"
  | "debug"
  | "info"
  | "warn"
  | "error"
  | "fatal";

/** Levels accepted by {@link TelemetryClient.captureMessage} */
export type TelemetryMessageLevel =
  | "fatal"
  | "error"
  | "warning"
  | "info"
  | "debug";

export type TelemetryEventKind =
  | "error"
  | "unhandled_rejection"
  | "route_error"
  | "resource_error"
  | "log"
  | "message"
  | "custom";

export interface ErrorPayload {
  name: string;
  message: string;
  stack?: string;
  cause?: unknown;
}

export interface TelemetryBreadcrumb {
  timestamp: string;
  category?: string;
  message: string;
  level?: TelemetryLevel;
  data?: Record<string, unknown>;
}

export interface RequestContext {
  url?: string;
  route?: string;
  userAgent?: string;
  requestId?: string;
  headers?: Record<string, string>;
}

export interface TelemetryContext {
  appId: string;
  environment?: string;
  release?: string;
  runtime?: TelemetryRuntime;
  userId?: string;
  sessionId?: string;
  route?: string;
  url?: string;
  userAgent?: string;
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
}

export interface TelemetryEvent {
  id: string;
  timestamp: string;
  runtime: TelemetryRuntime;
  kind: TelemetryEventKind;
  framework: TelemetryFramework;
  context: TelemetryContext;
  /** Present for error-class kinds */
  error?: ErrorPayload;
  /** Present for log, message, and custom kinds */
  message?: string;
  level?: TelemetryLevel;
  attributes?: Record<string, unknown>;
  componentStack?: string;
  routeId?: string;
  handled?: boolean;
  metadata?: Record<string, unknown>;
  /** Attached to error-class events when breadcrumbs are enabled */
  breadcrumbs?: TelemetryBreadcrumb[];
}

/** Provider kind from plan config (`console`, `http`, `sentry`, …). */
export type TelemetryProviderType = "console" | "http" | "callback" | "sentry";

export interface TelemetryProvider {
  readonly id: string;
  readonly type: TelemetryProviderType;
  /** Optional eager setup (e.g. Sentry.init) — run once after the runtime client is created */
  prepare?(): void | Promise<void>;
  send(event: TelemetryEvent): void | Promise<void>;
  flush?(): void | Promise<void>;
}

export interface CaptureOptions {
  framework?: TelemetryFramework;
  componentStack?: string;
  routeId?: string;
  handled?: boolean;
  metadata?: Record<string, unknown>;
}

export interface BreadcrumbsConfig {
  /** Default true */
  enabled?: boolean;
  /** Max entries retained; default 50 */
  maxSize?: number;
}

export function isErrorEventKind(
  kind: TelemetryEventKind,
): kind is
  | "error"
  | "unhandled_rejection"
  | "route_error"
  | "resource_error" {
  return (
    kind === "error" ||
    kind === "unhandled_rejection" ||
    kind === "route_error" ||
    kind === "resource_error"
  );
}

export function isLogEventKind(
  kind: TelemetryEventKind,
): kind is "log" | "message" | "custom" {
  return kind === "log" || kind === "message" || kind === "custom";
}
